import { Shadow, Text, Skeleton, SkeletonTypes, HStack, Stack, VStack } from '@genial/components';
import { ColorTypes, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Progress } from 'native-base';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { homeGlobalState } from '../../../../../../store/home/capture/atom';

export declare namespace ProductPercentageCardTypes {
  interface ProductItem {
    position: number;
    name: string;
    value: number;
    type: string;
    maxAmount: number;
  }

  interface Props {
    loading: boolean;
    color: ColorTypes.Option;
    product: ProductItem;
  }
}

const ColoredSkeleton = (props: SkeletonTypes.Props) => (
  <Skeleton {...props} endColor="brand.primary.lightest" />
);

export const ProductPercentageCard = ({
  color,
  loading,
  product,
}: ProductPercentageCardTypes.Props) => {
  const { valueVisibility } = useRecoilValue(homeGlobalState);

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  const renderLateralBar = (currentColor: ColorTypes.Option) => (
    <Box
      position={'absolute'}
      top={0}
      left={0}
      bottom={0}
      width={'4px'}
      backgroundColor={currentColor}
      borderTopLeftRadius={'full'}
      borderBottomLeftRadius={'full'}
    />
  );

  if (loading) {
    return (
      <Shadow level={1} borderRadius="8px">
        <HStack
          position={'relative'}
          borderRadius={'8px'}
          alignItems="center"
          justifyContent={'space-between'}
          padding="inset.xs">
          {renderLateralBar('brand.primary.lightest')}
          <HStack
            width="full"
            alignItems={'center'}
            space={Utils.Responsive.DesktopPhone({
              desktop: 'inline.xs',
              phone: 'inline.xxxs',
            })}>
            <HStack flex={1}>
              <Stack
                flex={1}
                direction={Utils.Responsive.DesktopPhone({ desktop: 'row', phone: 'column' })}
                space="inset.nano"
                justifyContent="space-between">
                <ColoredSkeleton
                  width={Utils.Responsive.DesktopPhone({ desktop: 100, phone: '80%' })}
                  rounded={'full'}
                  height={14}
                />
                <ColoredSkeleton
                  width={Utils.Responsive.DesktopPhone({ desktop: 100, phone: '50%' })}
                  rounded={'full'}
                  height={14}
                />
              </Stack>
            </HStack>
          </HStack>
        </HStack>
      </Shadow>
    );
  }

  return (
    <VStack space="stack.nano" marginY="stack.nano">
      <Shadow level={1} borderRadius="8px">
        <HStack
          position={'relative'}
          borderRadius={'8px'}
          alignItems="center"
          justifyContent={'space-between'}
          padding="inset.xs">
          {renderLateralBar(color)}
          <HStack width="full" alignItems={'center'}>
            <VStack flex={1}>
              <Stack flex={1} alignItems="center" direction="row" space="inset.nano">
                <Box
                  width="21px"
                  height="24px"
                  backgroundColor="brand.tertiary.principal"
                  borderRadius="8px"
                  alignItems="center"
                  justifyContent="center">
                  <Text color="neutral.white" fontWeight="bold">
                    {product.position}°
                  </Text>
                </Box>
                <Text fontSize="md" ml="inline.nano" textTransform="uppercase" variant="semibold">
                  {truncateText(product.name, 25)}
                </Text>
                <Text fontSize="sm" color="neutral.darkest" textTransform="capitalize">
                  {product.type}
                </Text>
                <HStack flex={1} alignItems="center" justifyContent="flex-end">
                  <Text
                    color={valueVisibility ? 'brand.primary.dark' : 'neutral.darkest'}
                    variant="semibold"
                    fontSize={valueVisibility ? 'lg' : 'xxs'}
                    letterSpacing={valueVisibility ? undefined : 'bullet.spacing'}>
                    {valueVisibility
                      ? Utils.Mask.ScaleMoney(product.value)
                      : Typography['bullet.sequence']}
                  </Text>
                </HStack>
              </Stack>
              <Stack
                flex={1}
                alignItems="center"
                direction="row"
                space="inset.nano"
                mt="inline.nano">
                <Box flex={1}>
                  <Progress
                    bg="neutral.white"
                    h={1}
                    _filledTrack={{ bg: 'brand.tertiary.principal' }}
                    value={(product.value / product.maxAmount) * 100}
                  />
                </Box>
              </Stack>
            </VStack>
          </HStack>
        </HStack>
      </Shadow>
    </VStack>
  );
};
